/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {
  getContracts,
  getContract,
  getLegacyContracts,
  getLegacyContract,
  getContractStatsByCompanyId,
  reserveContractCode,
  getContractsByCompany,
} from './api'
import {contractKeys} from './keys'
import {LegacyContractDataResponse} from './types'

export const useContracts = () =>
  useQuery({
    queryKey: contractKeys.list(),
    queryFn: getContracts,
  })

export const useContract = (id?: string) =>
  useQuery({
    queryKey: id ? contractKeys.detail(id) : [],
    queryFn: () => getContract(id!),
    enabled: !!id,
  })

export const useContractByCompany = (companyId?: string) =>
  useQuery({
    queryKey: contractKeys.listByCompany(companyId || ''),
    queryFn: () => getContractsByCompany(companyId!),
    enabled: !!companyId,
  })

export const useLegacyContracts = () =>
  useQuery({
    queryKey: contractKeys.legacy(),
    queryFn: getLegacyContracts,
  })

export const useLegacyContract = (id?: string) => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: id ? [...contractKeys.legacy(), id] : [],
    queryFn: () => getLegacyContract(id!),
    enabled: !!id,
    initialData: () => {
      if (!id) return undefined

      const list = queryClient.getQueryData<LegacyContractDataResponse[]>(contractKeys.legacy())

      return list?.find(item => item.contractId === id)
    },
    staleTime: 5 * 60 * 1000,
  })
}

export const useContractStats = (companyId?: string) =>
  useQuery({
    queryKey: companyId ? contractKeys.stats(companyId) : [],
    queryFn: () => getContractStatsByCompanyId(companyId!),
    enabled: !!companyId,
  })

export const useReserveContractCode = (companyId: string, enabled = true) =>
  useQuery({
    queryKey: contractKeys.reserveCode(companyId),
    queryFn: () => reserveContractCode(companyId),
    enabled,
  })
